/**
 * PiHOT product detail — sections/pihot-main-product.liquid
 *
 * <p-gallery>  tabbed media groups, scroll-snap tracks, arrows, per-panel thumbs
 * <p-qty>      quantity stepper (main form + mirrored copy in the sticky bar)
 * <p-addons>   ticked accessories, added together with the main item
 * <p-product>  variant switching (section re-render), Buy now, sticky buy bar
 *              (also the quick view body: data-view="quick-view", URL left alone)
 *
 * Add to cart stays with Vision's <product-form> (cart drawer + partial refresh).
 */
(() => {
  const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const REGIONS = ['PPrice', 'PPicker', 'PStock', 'PStickyInfo', 'PStickyPrice', 'PVariant'];

  /* Gallery ---------------------------------------------------------------- */

  class PGallery extends HTMLElement {
    connectedCallback() {
      this.abort = new AbortController();
      const { signal } = this.abort;
      this.panels = [...this.querySelectorAll('.p-gallery__panel')];
      this.tabs = [...this.querySelectorAll('[data-p-tab]')];
      this.prevBtn = this.querySelector('[data-p-prev]');
      this.nextBtn = this.querySelector('[data-p-next]');
      this.zoomBtn = this.querySelector('[data-p-zoom]');
      this.lightbox = this.querySelector('[data-p-lightbox]');
      this.active = this.panels.find((panel) => !panel.hidden) || this.panels[0];
      if (!this.active) return;

      this.prevBtn?.addEventListener('click', () => this.step(-1), { signal });
      this.nextBtn?.addEventListener('click', () => this.step(1), { signal });
      this.zoomBtn?.addEventListener('click', () => this.openLightbox(), { signal });

      this.tabs.forEach((tab) => {
        tab.addEventListener('click', () => this.select(Number(tab.dataset.pTab)), { signal });
      });
      this.querySelector('[role="tablist"]')?.addEventListener('keydown', (event) => this.onTabKey(event), { signal });

      this.panels.forEach((panel) => {
        this.trackOf(panel).addEventListener('scroll', () => this.onScroll(panel), { passive: true, signal });
      });

      this.querySelectorAll('[data-p-thumbs]').forEach((row) => {
        row.addEventListener('scroll', () => this.markOverflow(row), { passive: true, signal });
        /* Warm the matching stage image on thumb hover / focus so a click is instant. */
        row.addEventListener('pointerenter', (event) => {
          const thumb = event.target.closest('[data-p-thumb]');
          if (thumb) this.warmSlide(Number(thumb.dataset.pThumb));
        }, { signal, capture: true });
        row.addEventListener('focusin', (event) => {
          const thumb = event.target.closest('[data-p-thumb]');
          if (thumb) this.warmSlide(Number(thumb.dataset.pThumb));
        }, { signal });
      });
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this.markOverflow(this.thumbsOf()));
        this.resizeObserver.observe(this);
      }

      this.preloadCache = new Set();
      this.lightboxToken = 0;

      this.addEventListener('click', (event) => {
        const thumb = event.target.closest('[data-p-thumb]');
        if (thumb) {
          this.warmSlide(Number(thumb.dataset.pThumb));
          this.goTo(Number(thumb.dataset.pThumb));
          return;
        }
        const poster = event.target.closest('[data-p-play]');
        if (poster) this.playExternal(poster);
      }, { signal });

      this.querySelector('.p-gallery__stage').addEventListener('keydown', (event) => {
        if (event.target.closest('[role="tab"], [data-p-thumb], model-viewer, video, iframe')) return;
        if (event.key === 'ArrowLeft') this.step(-1);
        else if (event.key === 'ArrowRight') this.step(1);
      }, { signal });

      this.bindLightbox(signal);

      const start = Number(this.dataset.start) || 0;
      if (start) requestAnimationFrame(() => this.goTo(start, false));
      this.update();
    }

    disconnectedCallback() {
      this.abort?.abort();
      this.resizeObserver?.disconnect();
      if (this.lightbox?.open) this.closeLightbox();
    }

    bindLightbox(signal) {
      const box = this.lightbox;
      if (!box) return;
      box.querySelector('[data-p-lightbox-close]')?.addEventListener('click', () => this.closeLightbox(), { signal });
      box.querySelector('[data-p-lightbox-prev]')?.addEventListener('click', () => this.lightboxStep(-1), { signal });
      box.querySelector('[data-p-lightbox-next]')?.addEventListener('click', () => this.lightboxStep(1), { signal });
      box.querySelector('[data-p-lightbox-thumbs]')?.addEventListener('click', (event) => {
        const thumb = event.target.closest('[data-p-lightbox-thumb]');
        if (thumb) this.lightboxGoTo(Number(thumb.dataset.pLightboxThumb));
      }, { signal });
      box.querySelector('[data-p-lightbox-thumbs]')?.addEventListener('pointerenter', (event) => {
        const thumb = event.target.closest('[data-p-lightbox-thumb]');
        if (!thumb || !this.lightboxList) return;
        const item = this.lightboxList[Number(thumb.dataset.pLightboxThumb)];
        if (item) this.preloadUrl(item.src);
      }, { signal, capture: true });
      box.addEventListener('cancel', (event) => {
        event.preventDefault();
        this.closeLightbox();
      }, { signal });
      box.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') { event.preventDefault(); this.lightboxStep(-1); }
        else if (event.key === 'ArrowRight') { event.preventDefault(); this.lightboxStep(1); }
      }, { signal });

      const stage = box.querySelector('.p-lightbox__stage');
      if (!stage) return;
      let startX = 0;
      stage.addEventListener('pointerdown', (event) => {
        if (event.pointerType === 'mouse' && event.button !== 0) return;
        startX = event.clientX;
      }, { signal });
      stage.addEventListener('pointerup', (event) => {
        const delta = event.clientX - startX;
        if (Math.abs(delta) < 48) return;
        this.lightboxStep(delta < 0 ? 1 : -1);
      }, { signal });
    }

    /** Rewrite a Shopify CDN image URL to a specific width. */
    imageUrlAt(url, width) {
      if (!url) return '';
      if (/([?&])width=\d+/.test(url)) return url.replace(/([?&])width=\d+/, `$1width=${width}`);
      return url + (url.includes('?') ? '&' : '?') + `width=${width}`;
    }

    /** Warm a URL into the browser cache (deduped). */
    preloadUrl(url) {
      if (!url || this.preloadCache.has(url)) return;
      this.preloadCache.add(url);
      const img = new Image();
      img.decoding = 'async';
      img.src = url;
    }

    /** Make a gallery slide eager and decode it; also warm the lightbox size. */
    warmSlide(index, panel = this.active) {
      const slide = this.slidesOf(panel)[index];
      const img = slide?.querySelector('img');
      if (!img) return;
      if (img.loading === 'lazy') img.loading = 'eager';
      const base = img.getAttribute('src') || img.currentSrc || img.src;
      if (img.decode) img.decode().catch(() => {});
      this.preloadUrl(base);
      /* Same 1400 width as the gallery src — lightbox reuses this cache entry. */
      this.preloadUrl(this.imageUrlAt(base, 1400));
    }

    warmAround(index, radius = 2, panel = this.active) {
      const count = this.slidesOf(panel).length;
      for (let i = index - radius; i <= index + radius; i += 1) {
        if (i >= 0 && i < count) this.warmSlide(i, panel);
      }
    }

    lightboxItems(panel = this.active) {
      return this.slidesOf(panel).map((slide, slideIndex) => {
        const img = slide.querySelector('img');
        if (!img) return null;
        const src = img.getAttribute('src') || img.currentSrc || img.src;
        return {
          slideIndex,
          /* Match gallery max width so CDN responses are already cached. */
          src: this.imageUrlAt(src, 1400),
          thumb: this.imageUrlAt(src, 160),
          alt: img.alt || ''
        };
      }).filter(Boolean);
    }

    openLightbox() {
      const box = this.lightbox;
      if (!box || typeof box.showModal !== 'function') return;
      const items = this.lightboxItems();
      if (!items.length) return;

      let index = items.findIndex((item) => item.slideIndex === this.indexOf());
      if (index < 0) index = 0;

      const thumbs = box.querySelector('[data-p-lightbox-thumbs]');
      if (thumbs) {
        thumbs.hidden = items.length < 2;
        thumbs.innerHTML = items.map((item, i) => (
          `<button type="button" class="p-lightbox__thumb${i === index ? ' is-active' : ''}" data-p-lightbox-thumb="${i}" aria-label="${i + 1}" aria-current="${i === index ? 'true' : 'false'}">` +
          `<img src="${item.thumb}" alt="" width="72" height="72" loading="eager" decoding="async">` +
          `</button>`
        )).join('');
      }
      box.classList.toggle('p-lightbox--solo', items.length < 2);
      this.lightboxIndex = index;
      this.lightboxList = items;
      this.lightboxToken += 1;

      /* Seed the visible frame from the gallery CDN URL (usually already cached). */
      const shown = box.querySelector('[data-p-lightbox-img]');
      const buffer = box.querySelector('[data-p-lightbox-buf]');
      if (shown && buffer) {
        shown.src = items[index].src;
        shown.alt = items[index].alt;
        shown.classList.add('is-shown');
        shown.removeAttribute('aria-hidden');
        buffer.classList.remove('is-shown');
        buffer.removeAttribute('src');
        buffer.setAttribute('aria-hidden', 'true');
      }
      this.syncLightboxChrome();
      this.preloadLightboxAround(index);
      if (!box.open) box.showModal();
      box.querySelector('[data-p-lightbox-close]')?.focus();
      document.documentElement.classList.add('p-lightbox-open');
    }

    closeLightbox() {
      const box = this.lightbox;
      if (!box?.open) return;
      this.lightboxToken += 1;
      box.close();
      document.documentElement.classList.remove('p-lightbox-open');
      this.zoomBtn?.focus();
    }

    lightboxStep(delta) {
      if (!this.lightboxList?.length) return;
      this.lightboxGoTo(this.lightboxIndex + delta);
    }

    lightboxGoTo(index) {
      if (!this.lightboxList?.length) return;
      const next = Math.max(0, Math.min(this.lightboxList.length - 1, index));
      if (next === this.lightboxIndex) return;
      this.lightboxIndex = next;
      this.renderLightbox();
      this.preloadLightboxAround(next);
      this.goTo(this.lightboxList[next].slideIndex, false);
    }

    preloadLightboxAround(index) {
      const items = this.lightboxList;
      if (!items?.length) return;
      for (let i = index - 2; i <= index + 2; i += 1) {
        if (items[i]) this.preloadUrl(items[i].src);
      }
    }

    syncLightboxChrome() {
      const box = this.lightbox;
      const items = this.lightboxList;
      if (!box || !items?.length) return;
      const prev = box.querySelector('[data-p-lightbox-prev]');
      const next = box.querySelector('[data-p-lightbox-next]');
      if (prev) prev.disabled = this.lightboxIndex <= 0;
      if (next) next.disabled = this.lightboxIndex >= items.length - 1;
      box.querySelectorAll('[data-p-lightbox-thumb]').forEach((thumb, i) => {
        const current = i === this.lightboxIndex;
        thumb.classList.toggle('is-active', current);
        thumb.setAttribute('aria-current', current ? 'true' : 'false');
        if (current) this.centerThumb(thumb.parentElement, thumb);
      });
    }

    /**
     * Double-buffer swap: load into the hidden <img>, then flip .is-shown.
     * The visible frame never goes blank, so switches feel instant when cached.
     */
    renderLightbox() {
      const box = this.lightbox;
      const items = this.lightboxList;
      if (!box || !items?.length) return;
      const item = items[this.lightboxIndex];
      const shown = box.querySelector('.p-lightbox__img.is-shown') || box.querySelector('[data-p-lightbox-img]');
      const buffer = [...box.querySelectorAll('.p-lightbox__img')].find((el) => el !== shown)
        || box.querySelector('[data-p-lightbox-buf]');

      this.syncLightboxChrome();
      if (!shown || !buffer) return;
      if (shown.src === item.src && shown.complete && shown.naturalWidth) {
        shown.alt = item.alt;
        return;
      }

      const token = ++this.lightboxToken;
      const reveal = () => {
        if (token !== this.lightboxToken) return;
        buffer.alt = item.alt;
        buffer.classList.add('is-shown');
        buffer.removeAttribute('aria-hidden');
        shown.classList.remove('is-shown');
        shown.setAttribute('aria-hidden', 'true');
      };

      if (buffer.src === item.src && buffer.complete && buffer.naturalWidth) {
        reveal();
        return;
      }

      const onReady = () => {
        buffer.removeEventListener('load', onReady);
        buffer.removeEventListener('error', onReady);
        if (buffer.decode) {
          buffer.decode().then(reveal).catch(reveal);
        } else {
          reveal();
        }
      };

      buffer.addEventListener('load', onReady);
      buffer.addEventListener('error', onReady);
      buffer.src = item.src;
      if (buffer.complete && buffer.naturalWidth) onReady();
    }

    trackOf(panel) {
      return panel.querySelector('[data-p-track]');
    }

    thumbsOf(panel = this.active) {
      return this.querySelector(`[data-p-thumbs="${panel.dataset.index}"]`);
    }

    dotsOf(panel = this.active) {
      return this.querySelector(`[data-p-dots="${panel.dataset.index}"]`);
    }

    /** Fade the row edges that hide more thumbnails. */
    markOverflow(row) {
      if (!row || row.hidden) return;
      const max = row.scrollWidth - row.clientWidth;
      const more = [];
      if (row.scrollLeft > 1) more.push('start');
      if (row.scrollLeft < max - 1) more.push('end');
      row.dataset.more = more.join(' ');
    }

    slidesOf(panel) {
      return [...this.trackOf(panel).children];
    }

    indexOf(panel = this.active) {
      const track = this.trackOf(panel);
      return track.clientWidth ? Math.round(track.scrollLeft / track.clientWidth) : 0;
    }

    goTo(index, smooth = true) {
      const track = this.trackOf(this.active);
      const count = this.slidesOf(this.active).length;
      const target = Math.max(0, Math.min(count - 1, index));
      track.scrollTo({ left: target * track.clientWidth, behavior: smooth && !reduceMotion() ? 'smooth' : 'auto' });
      if (!smooth) this.update();
    }

    step(delta) {
      this.goTo(this.indexOf() + delta);
    }

    onScroll(panel) {
      if (panel !== this.active) return;
      cancelAnimationFrame(this.frame);
      this.frame = requestAnimationFrame(() => this.update());
    }

    update() {
      const index = this.indexOf();
      const slides = this.slidesOf(this.active);

      if (this.prevBtn) this.prevBtn.disabled = index <= 0;
      if (this.nextBtn) this.nextBtn.disabled = index >= slides.length - 1;

      const row = this.thumbsOf();
      if (row) {
        row.querySelectorAll('[data-p-thumb]').forEach((thumb, i) => {
          const current = i === index;
          thumb.classList.toggle('is-active', current);
          if (current) {
            thumb.setAttribute('aria-current', 'true');
            this.centerThumb(row, thumb);
          } else {
            thumb.removeAttribute('aria-current');
          }
        });
        this.markOverflow(row);
      }

      this.dotsOf()?.querySelectorAll('.p-gallery__dot').forEach((dot, i) => dot.classList.toggle('is-active', i === index));

      const count = this.querySelector('[data-p-count]');
      if (count) {
        count.hidden = slides.length < 2;
        count.textContent = `${index + 1}/${slides.length}`;
      }

      if (this.zoomBtn) this.zoomBtn.hidden = !this.lightboxItems().length;

      this.warmAround(index, 2);

      slides.forEach((slide, i) => {
        if (i !== index) this.stopMedia(slide);
      });
    }

    /** Slide the row so the active thumb sits in the middle, revealing its neighbours. */
    centerThumb(row, thumb) {
      if (row.scrollWidth <= row.clientWidth) return;
      const offset = thumb.getBoundingClientRect().left - row.getBoundingClientRect().left;
      const left = row.scrollLeft + offset - (row.clientWidth - thumb.offsetWidth) / 2;
      if (Math.abs(left - row.scrollLeft) < 1) return;
      row.scrollTo({ left, behavior: reduceMotion() ? 'auto' : 'smooth' });
    }

    select(panelIndex, focusTab = false) {
      const next = this.panels[panelIndex];
      if (!next || next === this.active) return;
      const previous = this.active;

      previous.hidden = true;
      this.slidesOf(previous).forEach((slide) => this.stopMedia(slide));
      next.hidden = false;
      const previousRow = this.thumbsOf(previous);
      const nextRow = this.thumbsOf(next);
      const nextDots = this.dotsOf(next);
      [previousRow, this.dotsOf(previous)].forEach((el) => el && (el.hidden = true));
      [nextRow, nextDots].forEach((el) => el && (el.hidden = false));
      this.active = next;

      this.tabs.forEach((tab, i) => {
        const selected = i === panelIndex;
        tab.setAttribute('aria-selected', String(selected));
        tab.tabIndex = selected ? 0 : -1;
        if (selected && focusTab) tab.focus();
      });

      if (next.querySelector('model-viewer')) this.loadModels(next);

      const gsap = window.gsap;
      if (gsap && !reduceMotion()) {
        gsap.fromTo([next, nextDots].filter(Boolean), { opacity: 0 }, { opacity: 1, duration: 0.4, ease: 'power2.out', clearProps: 'opacity' });
      }
      this.warmAround(0, 2, next);
      this.update();
    }

    onTabKey(event) {
      const current = this.tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true');
      let target = null;
      if (event.key === 'ArrowRight') target = (current + 1) % this.tabs.length;
      else if (event.key === 'ArrowLeft') target = (current - 1 + this.tabs.length) % this.tabs.length;
      else if (event.key === 'Home') target = 0;
      else if (event.key === 'End') target = this.tabs.length - 1;
      if (target === null) return;
      event.preventDefault();
      this.select(target, true);
    }

    /** Bring a product media (variant image) into view, switching tab if needed. */
    showMedia(mediaId) {
      if (!mediaId) return;
      for (const panel of this.panels) {
        const slides = this.slidesOf(panel);
        const index = slides.findIndex((slide) => slide.dataset.mediaId === String(mediaId));
        if (index < 0) continue;
        if (panel !== this.active) this.select(this.panels.indexOf(panel));
        this.goTo(index);
        return;
      }
    }

    playExternal(poster) {
      const template = poster.nextElementSibling;
      if (!template || template.tagName !== 'TEMPLATE') return;
      poster.hidden = true;
      const frame = template.content.firstElementChild.cloneNode(true);
      if (frame.src) {
        frame.src += (frame.src.includes('?') ? '&' : '?') + 'autoplay=1';
      }
      poster.after(frame);
    }

    stopMedia(slide) {
      slide.querySelectorAll('video').forEach((video) => video.pause());
      const frame = slide.querySelector('iframe');
      const poster = slide.querySelector('[data-p-play]');
      if (frame && poster) {
        frame.remove();
        poster.hidden = false;
      }
    }

    loadModels(panel) {
      if (this.modelsLoaded || !window.Shopify?.loadFeatures) return;
      this.modelsLoaded = true;
      window.Shopify.loadFeatures([{
        name: 'model-viewer-ui',
        version: '1.0',
        onLoad: (errors) => {
          if (errors || !window.Shopify.ModelViewerUI) return;
          panel.querySelectorAll('model-viewer').forEach((model) => new window.Shopify.ModelViewerUI(model));
        }
      }]);
    }
  }

  /* Quantity --------------------------------------------------------------- */

  class PQty extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');
      this.minus = this.querySelector('[data-step="-1"]');
      this.plus = this.querySelector('[data-step="1"]');
      this.abort = new AbortController();
      const { signal } = this.abort;
      this.addEventListener('click', (event) => {
        const button = event.target.closest('[data-step]');
        if (button) this.set(Number(this.input.value) + Number(button.dataset.step));
      }, { signal });
      this.input.addEventListener('change', () => this.set(this.input.value), { signal });
      this.sync(this.input.value);
    }

    disconnectedCallback() {
      this.abort?.abort();
    }

    clamp(value) {
      const min = Number(this.input.min) || 1;
      const max = this.input.max ? Number(this.input.max) : Infinity;
      return Math.min(max, Math.max(min, parseInt(value, 10) || min));
    }

    sync(value) {
      const next = this.clamp(value);
      this.input.value = next;
      this.minus?.classList.toggle('disabled', next <= (Number(this.input.min) || 1));
      this.plus?.classList.toggle('disabled', this.input.max !== '' && next >= Number(this.input.max));
      return next;
    }

    set(value) {
      const next = this.sync(value);
      this.dispatchEvent(new CustomEvent('p-qty:change', { bubbles: true, detail: { value: next } }));
    }
  }

  /* Add-ons ---------------------------------------------------------------- */

  const formatMoney = (cents) => {
    const format = window.theme?.settings?.money_with_currency_format;
    return typeof window.formatMoney === 'function' && format ? window.formatMoney(cents, format) : null;
  };

  class PAddons extends HTMLElement {
    connectedCallback() {
      this.abort = new AbortController();
      const { signal } = this.abort;
      this.rows = [...this.querySelectorAll('[data-p-addon]')];
      this.sum = this.querySelector('[data-p-addons-sum]');

      document.getElementById(this.dataset.form)?.addEventListener('formdata', (event) => this.inject(event.formData), { signal });

      this.addEventListener('change', (event) => {
        const row = event.target.closest('[data-p-addon]');
        if (!row) return;
        if (event.target.matches('[data-p-addon-variant]')) this.selectVariant(row, event.target);
        this.update();
      }, { signal });

      /* Changing an accessory's quantity also ticks it. */
      this.addEventListener('p-qty:change', (event) => {
        const check = event.target.closest('[data-p-addon]')?.querySelector('.p-addon__check');
        if (check && !check.checked) check.checked = true;
        this.update();
      }, { signal });

      this.update(false);
    }

    disconnectedCallback() {
      this.abort?.abort();
    }

    picks() {
      return this.rows
        .filter((row) => row.querySelector('.p-addon__check').checked)
        .map((row) => ({
          id: row.dataset.variant,
          price: Number(row.dataset.price) || 0,
          quantity: Math.max(1, parseInt(row.querySelector('.p-qty__input').value, 10) || 1)
        }));
    }

    total() {
      return this.picks().reduce((sum, pick) => sum + pick.price * pick.quantity, 0);
    }

    selectVariant(row, select) {
      const option = select.selectedOptions[0];
      if (!option) return;
      row.dataset.variant = option.value;
      row.dataset.price = option.dataset.cents;
      const price = row.querySelector('[data-p-addon-price]');
      const current = document.createElement('span');
      current.textContent = option.dataset.price;
      const nodes = [current];
      if (option.dataset.compare) {
        const compare = document.createElement('s');
        compare.textContent = option.dataset.compare;
        nodes.push(compare);
      }
      price.replaceChildren(...nodes);

      const image = row.querySelector('.p-addon__media img');
      if (image && option.dataset.image) {
        image.removeAttribute('srcset');
        image.src = option.dataset.image;
      }
    }

    update(notify = true) {
      const picks = this.picks();
      if (this.sum) {
        const count = picks.length;
        const amount = formatMoney(this.total());
        this.sum.hidden = !count || !amount;
        if (count && amount) {
          this.sum.textContent = (this.dataset.summary || '').replace('[count]', count).replace('[amount]', amount);
        }
      }
      if (notify) this.dispatchEvent(new CustomEvent('p-addons:change', { bubbles: true }));
    }

    /* Main item + ticked accessories as one items[] request (the main item first,
       keeping its selling plan and line item properties). */
    inject(formData) {
      const picks = this.picks();
      const id = formData.get('id');
      if (!picks.length || !id) return;

      const moved = [...formData.entries()].filter(([key]) =>
        key === 'id' || key === 'quantity' || key === 'selling_plan' || key.startsWith('properties['));
      new Set(moved.map(([key]) => key)).forEach((key) => formData.delete(key));
      moved.forEach(([key, value]) => {
        const name = key.startsWith('properties[') ? `items[0][properties]${key.slice(10)}` : `items[0][${key}]`;
        formData.append(name, value);
      });
      if (!moved.some(([key]) => key === 'quantity')) formData.append('items[0][quantity]', '1');

      picks.forEach((pick, i) => {
        formData.append(`items[${i + 1}][id]`, pick.id);
        formData.append(`items[${i + 1}][quantity]`, String(pick.quantity));
      });
    }
  }

  class PDeliveryNote extends HTMLElement {
    connectedCallback() {
      const price = Number(this.dataset.price);
      const minimum = Number(this.dataset.minimum);
      const currency = window.Shopify?.currency;
      const rate = this.dataset.currency === this.dataset.baseCurrency
        ? 1
        : currency?.active === this.dataset.currency ? Number(currency.rate) : NaN;
      const eligible = Number.isFinite(price) && Number.isFinite(minimum) && minimum > 0
        && Number.isFinite(rate) && rate > 0 && price >= Math.round(minimum * rate);
      const calculated = this.querySelector('[data-p-delivery-calculated]');
      const free = this.querySelector('[data-p-delivery-free]');
      if (!calculated || !free) return;
      calculated.hidden = eligible;
      free.hidden = !eligible;
    }
  }

  /* Product ---------------------------------------------------------------- */

  class PProduct extends HTMLElement {
    connectedCallback() {
      this.sid = this.dataset.section;
      this.abort = new AbortController();
      const { signal } = this.abort;
      this.form = document.getElementById(this.dataset.form);
      this.gallery = this.querySelector('p-gallery');
      this.sticky = this.querySelector('p-sticky-buy');

      this.addEventListener('change', (event) => {
        if (event.target.matches('.p-option__input')) this.onOptionChange(event.target);
      }, { signal });

      document.addEventListener('click', (event) => {
        const button = event.target.closest('[data-p-buy-now]');
        if (button && this.owns(button)) this.buyNow(button);
      }, { signal });

      document.addEventListener('p-qty:change', (event) => {
        if (!this.owns(event.target) || !event.target.matches('[data-p-qty]')) return;
        this.qtyElements().forEach((qty) => {
          if (qty !== event.target) qty.sync(event.detail.value);
        });
        this.updateBuyPrice();
      }, { signal });

      this.addEventListener('p-addons:change', () => this.updateBuyPrice(), { signal });

      if (this.sticky) this.mountSticky();
    }

    disconnectedCallback() {
      this.abort?.abort();
      this.request?.abort();
      this.observer?.disconnect();
      this.footerObserver?.disconnect();
      this.resizeObserver?.disconnect();
      this.sticky?.remove();
      document.body.classList.remove('p-sticky-buy-on');
    }

    owns(element) {
      return this.contains(element) || Boolean(this.sticky?.contains(element));
    }

    qtyElements() {
      return [...this.querySelectorAll('p-qty[data-p-qty]'), ...(this.sticky ? this.sticky.querySelectorAll('p-qty[data-p-qty]') : [])];
    }

    /* Variant switching */

    async onOptionChange(input) {
      const ids = [...this.querySelectorAll('.p-option__input:checked')].map((el) => el.dataset.optionValueId);
      const focusId = input.dataset.optionValueId;
      const picker = document.getElementById(`PPicker-${this.sid}`);
      picker?.setAttribute('aria-busy', 'true');

      this.request?.abort();
      this.request = new AbortController();

      try {
        const view = this.dataset.view ? `&view=${this.dataset.view}` : '';
        const response = await fetch(`${this.dataset.url}?option_values=${ids.join(',')}&section_id=${this.sid}${view}`, { signal: this.request.signal });
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');

        REGIONS.forEach((prefix) => {
          const id = `${prefix}-${this.sid}`;
          const current = document.getElementById(id);
          const next = html.getElementById(id);
          if (current && next) current.replaceWith(next);
        });

        const focusTarget = document.querySelector(`#PPicker-${this.sid} [data-option-value-id="${focusId}"]`);
        if (focusTarget && (document.activeElement === document.body || !document.activeElement?.isConnected)) {
          focusTarget.focus({ preventScroll: true });
        }

        this.applyVariant();
      } catch (error) {
        if (error.name !== 'AbortError') console.error(error);
      } finally {
        document.getElementById(`PPicker-${this.sid}`)?.removeAttribute('aria-busy');
      }
    }

    readVariant() {
      try {
        return JSON.parse(document.getElementById(`PVariant-${this.sid}`).textContent);
      } catch {
        return null;
      }
    }

    applyVariant() {
      const variant = this.readVariant();
      if (!variant) return;
      const buyable = variant.exists && variant.available;

      const idInput = this.form?.querySelector('[name="id"]');
      if (idInput && variant.exists) idInput.value = variant.id;

      const scope = [this, this.sticky].filter(Boolean);
      scope.forEach((el) => {
        el.querySelectorAll('[data-p-add], [data-p-buy-now]').forEach((button) => {
          button.disabled = !buyable;
        });
        el.querySelectorAll('[data-p-add-label]').forEach((label) => {
          label.textContent = variant.label;
        });
        el.querySelectorAll('[data-p-buy-price]').forEach((price) => {
          price.hidden = !buyable;
        });
      });
      this.updateBuyPrice(variant);

      if (variant.exists) {
        if (!this.dataset.view && !('noHistory' in this.dataset)) {
          const url = new URL(window.location.href);
          url.searchParams.set('variant', variant.id);
          window.history.replaceState(window.history.state, '', url.toString());
        }
        this.gallery?.showMedia(variant.media);
      }

      this.dispatchEvent(new CustomEvent('pihot:variant-change', { bubbles: true, detail: variant }));
    }

    /* Buttons and the sticky bar carry the order total (unit price × quantity +
       ticked add-ons); the price module keeps the unit price. */

    updateBuyPrice(variant = this.readVariant()) {
      if (!variant) return;
      const quantity = Math.max(1, Number(this.form?.querySelector('[name="quantity"]')?.value) || 1);
      const addonsEl = this.querySelector('p-addons');
      const addons = addonsEl?.total?.() || 0;
      const addonCount = addonsEl?.picks?.().length || 0;
      const cents = (quantity > 1 || addons > 0) && Number.isFinite(variant.price_cents)
        ? variant.price_cents * quantity + addons
        : null;
      const total = (cents !== null && formatMoney(cents)) || variant.price;
      [this, this.sticky].filter(Boolean).forEach((el) => {
        el.querySelectorAll('[data-p-buy-price]').forEach((price) => {
          price.textContent = total;
        });
      });
      this.updateStickyTotal(variant.exists && variant.available ? cents : null, quantity, variant.price, addonCount);
    }

    /* Sticky bar: unit price on its own; the total plus "2 × $369.00 · Incl. 2 add-ons" once it differs. */

    updateStickyTotal(cents, quantity, unit, addonCount) {
      const sum = this.sticky?.querySelector('[data-p-sticky-sum]');
      if (!sum) return;
      const box = sum.querySelector('.p-sticky-buy__total');
      const total = cents !== null && typeof window.formatMoney === 'function' && sum.dataset.moneyFormat
        ? window.formatMoney(cents, sum.dataset.moneyFormat)
        : null;
      sum.classList.toggle('is-total', Boolean(total));
      box.hidden = !total;
      if (!total) return;
      const parts = [];
      if (quantity > 1) parts.push((sum.dataset.qtyNote || '').replace('[count]', quantity).replace('[price]', unit));
      if (addonCount === 1) parts.push(sum.dataset.addonNote || '');
      else if (addonCount > 1) parts.push((sum.dataset.addonsNote || '').replace('[count]', addonCount));
      box.querySelector('[data-p-sticky-total]').textContent = total;
      box.querySelector('[data-p-sticky-note]').replaceChildren(...parts.filter(Boolean).map((text) => {
        const part = document.createElement('span');
        part.textContent = text;
        return part;
      }));
    }

    /* Buy now: add the current selection, then straight to checkout. */

    async buyNow(button) {
      if (!this.form || button.classList.contains('loading')) return;
      button.classList.add('loading');
      button.setAttribute('aria-disabled', 'true');
      const productForm = this.querySelector('product-form');
      productForm?.handleErrorMessage?.();

      try {
        const response = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: new FormData(this.form)
        });
        const data = await response.json();
        if (!response.ok || data.status) {
          productForm?.handleErrorMessage?.(data.description || data.message);
          throw new Error(data.description || data.message);
        }
        window.location.href = `${root}checkout`;
      } catch (error) {
        button.classList.remove('loading');
        button.removeAttribute('aria-disabled');
        console.error(error);
      }
    }

    /* Sticky buy bar: shown once the main buy row has scrolled off the top of the
       screen (not while it is still below the fold), hidden again once the footer
       scrolls into view. Lives at the end of #main-content. */

    mountSticky() {
      const main = document.getElementById('main-content') || document.body;
      main.appendChild(this.sticky);

      const setOffset = () => {
        const height = `${this.sticky.offsetHeight}px`;
        this.sticky.style.setProperty('--p-sticky-buy-h', height);
        document.body.style.setProperty('--p-sticky-buy-offset', height);
      };
      setOffset();
      this.resizeObserver = new ResizeObserver(setOffset);
      this.resizeObserver.observe(this.sticky);

      const buyRow = this.querySelector('[data-p-buy] .p-pdp__actions') || this.querySelector('[data-p-buy]');
      if (!buyRow) return;
      const footer = document.querySelector('.shopify-section-group-footer-group');
      const inView = new Map();

      const update = (entries) => {
        entries.forEach((entry) => inView.set(entry.target, entry.isIntersecting));
        const show = inView.get(buyRow) === false && !(footer && inView.get(footer));
        this.sticky.classList.toggle('is-visible', show);
        this.sticky.inert = !show;
        document.body.classList.toggle('p-sticky-buy-on', show);
      };
      /* The root extends far below the viewport, so the buy row only stops intersecting
         once it has scrolled off the top — also when an anchor jump skips past it. */
      this.observer = new IntersectionObserver(update, { rootMargin: '0px 0px 100000px 0px' });
      this.observer.observe(buyRow);
      if (footer) {
        /* Start leaving one bar-height early so the bar is gone before the footer shows. */
        const lead = Math.ceil(this.sticky.offsetHeight) || 80;
        this.footerObserver = new IntersectionObserver(update, { rootMargin: `0px 0px ${lead}px 0px` });
        this.footerObserver.observe(footer);
      }
    }
  }

  if (!customElements.get('p-gallery')) customElements.define('p-gallery', PGallery);
  if (!customElements.get('p-qty')) customElements.define('p-qty', PQty);
  if (!customElements.get('p-addons')) customElements.define('p-addons', PAddons);
  if (!customElements.get('p-delivery-note')) customElements.define('p-delivery-note', PDeliveryNote);
  if (!customElements.get('p-product')) customElements.define('p-product', PProduct);
})();
